import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { usePendingOnboardingAssets } from '@/hooks/usePendingOnboardingAssets';
import { isFeatureEnabledWithFallback } from '@/lib/featureEntitlements';
import { Loader2 } from 'lucide-react';

interface FeatureRouteProps {
  featureKey: string | null; // null means always accessible
  children: React.ReactNode;
  fallback?: string; // path to redirect to if feature is not enabled
  alsoAllowFeatureKey?: string;
  requireFeatureKey?: string;
}

export const FeatureRoute: React.FC<FeatureRouteProps> = ({
  featureKey,
  children,
  fallback = '/home',
  alsoAllowFeatureKey,
  requireFeatureKey,
}) => {
  const location = useLocation();
  const { data: entitlements, isLoading } = useOrganizationEntitlements();
  const { data: pendingAssets, isLoading: pendingAssetsLoading } = usePendingOnboardingAssets();
  const enabledFeatures = entitlements?.entitlements?.features || {};

  if (!featureKey && !requireFeatureKey) {
    return <>{children}</>;
  }

  if (isLoading || pendingAssetsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  const isFeatureOn = (key: string) => isFeatureEnabledWithFallback(enabledFeatures, key);

  const isEnabled = (!featureKey || isFeatureOn(featureKey)) ||
    (!!alsoAllowFeatureKey && isFeatureOn(alsoAllowFeatureKey));

  const meetsRequiredFeature = !requireFeatureKey || isFeatureOn(requireFeatureKey);

  const hasPendingTemplates = Array.isArray(pendingAssets?.pending_templates) && pendingAssets.pending_templates.length > 0;
  const hasPendingFlow = !!pendingAssets?.pending_flow;
  const hasActivatedOnboardingDrafts = !!pendingAssets?.activated_business_account_id;
  const isTemplatesRoute = location.pathname.startsWith('/home/templates');
  const isAutomationRoute = location.pathname.startsWith('/home/automation');
  const isBusinessAccountsRoute =
    location.pathname.startsWith('/home/business-accounts') ||
    location.pathname.startsWith('/home/integrations/whatsapp-business');
  const allowOnboardingAccess =
    (isTemplatesRoute && (hasPendingTemplates || hasActivatedOnboardingDrafts)) ||
    (isAutomationRoute && (hasPendingFlow || hasActivatedOnboardingDrafts)) ||
    (isBusinessAccountsRoute && (hasPendingTemplates || hasPendingFlow || hasActivatedOnboardingDrafts));

  if ((!isEnabled && !allowOnboardingAccess) || !meetsRequiredFeature) {
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};
